import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  NavLink,
  useHistory,
  useLocation,
} from "react-router-dom";

import {
  FaChevronDown,
  FaChevronUp,
  FaCrown,
  FaExclamationTriangle,
  FaRedoAlt,
  FaSignOutAlt,
  FaThumbtack,
  FaTimes,
  FaUserCircle,
} from "react-icons/fa";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import API from "../../../../utils/apiCalling";

import {
  config,
} from "../../../../utils/apiUrl";

import useAuth from "../../../../hooks/useAuth";

import {
  getCurrentSubscription,
} from "../../../../store/tenantSubscription/subscriptionSlice";

import {
  prepareMenuData,
} from "./menuData";

import "./AdminSidebar.css";


/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

const DESKTOP_BREAKPOINT =
  992;


/*
|--------------------------------------------------------------------------
| API INSTANCE
|--------------------------------------------------------------------------
*/

const api =
  new API();


/*
|--------------------------------------------------------------------------
| FORMAT PLAN DATE
|--------------------------------------------------------------------------
*/

const formatPlanDate = (
  value
) => {

  if (
    !value
  ) {
    return "";
  }


  const date =
    new Date(
      value
    );


  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }


  return date
    .toLocaleDateString(
      "en-IN",
      {
        day:
          "2-digit",

        month:
          "short",

        year:
          "numeric",
      }
    );

};


/*
|--------------------------------------------------------------------------
| ADMIN SIDEBAR
|--------------------------------------------------------------------------
*/

const AdminSidebar = ({
  pinnedOpen = false,

  onPinnedOpenChange,

  mobileOpen = false,

  onMobileOpenChange,
}) => {

  /*
  |--------------------------------------------------------------------------
  | REDUX
  |--------------------------------------------------------------------------
  */

  const dispatch =
    useDispatch();


  /*
  |--------------------------------------------------------------------------
  | AUTH
  |--------------------------------------------------------------------------
  */

  const {
    logout,
    user,
  } =
    useAuth();


  /*
  |--------------------------------------------------------------------------
  | ROUTER
  |--------------------------------------------------------------------------
  */

  const location =
    useLocation();


  const history =
    useHistory();


  /*
  |--------------------------------------------------------------------------
  | TENANT SUBSCRIPTION
  |--------------------------------------------------------------------------
  |
  | IMPORTANT:
  |
  | We are NOT calling the subscription API directly here.
  |
  | subscriptionSlice is the single source of truth.
  |
  |--------------------------------------------------------------------------
  */

  const {
    data:
      currentSubscription,

    loading:
      subscriptionLoading,

  } =
    useSelector(
      (
        state
      ) =>
        state
          ?.tenantSubscription
          ?.currentSubscription ||
        {
          data:
            null,

          loading:
            false,
        }
    );


  const subscriptionInitialized =
    useSelector(
      (
        state
      ) =>
        state
          ?.tenantSubscription
          ?.initialized ===
        true
    );


  /*
  |--------------------------------------------------------------------------
  | NAVIGATION STATE
  |--------------------------------------------------------------------------
  */

  const [
    navigation,
    setNavigation,
  ] =
    useState(
      []
    );


  const [
    navigationLoading,
    setNavigationLoading,
  ] =
    useState(
      true
    );


  const [
    navigationError,
    setNavigationError,
  ] =
    useState(
      ""
    );


  /*
  |--------------------------------------------------------------------------
  | DESKTOP STATE
  |--------------------------------------------------------------------------
  */

  const [
    isDesktop,
    setIsDesktop,
  ] =
    useState(
      () =>
        window.matchMedia(
          `(min-width: ${DESKTOP_BREAKPOINT}px)`
        )
          .matches
    );


  /*
  |--------------------------------------------------------------------------
  | HOVER EXPANDED
  |--------------------------------------------------------------------------
  */

  const [
    hoverExpanded,
    setHoverExpanded,
  ] =
    useState(
      false
    );


  /*
  |--------------------------------------------------------------------------
  | EXPANDED MENU
  |--------------------------------------------------------------------------
  */

  const [
    expandedMenu,
    setExpandedMenu,
  ] =
    useState(
      ""
    );


  /*
  |--------------------------------------------------------------------------
  | EFFECTIVE DESKTOP STATE
  |--------------------------------------------------------------------------
  */

  const desktopExpanded =
    isDesktop &&
    (
      pinnedOpen ||
      hoverExpanded
    );


  /*
  |--------------------------------------------------------------------------
  | SHOW TEXT
  |--------------------------------------------------------------------------
  */

  const showSidebarText =
    desktopExpanded ||
    !isDesktop;


  /*
  |--------------------------------------------------------------------------
  | LOAD NAVIGATION
  |--------------------------------------------------------------------------
  |
  | Backend decides:
  |
  | - subscription access
  | - owner access
  | - role permissions
  | - custom employee permissions
  |
  | Frontend only renders returned menu.
  |
  |--------------------------------------------------------------------------
  */

  const loadNavigation =
    useCallback(
      async () => {

        try {

          setNavigationLoading(
            true
          );


          setNavigationError(
            ""
          );


          const response =
            await api.get(
              config
                .tenantNavigationMenu
            );


          /*
          |--------------------------------------------------------------------------
          | ERROR RESPONSE
          |--------------------------------------------------------------------------
          */

          if (
            response
              ?.success ===
            false
          ) {

            throw new Error(
              response
                ?.message ||
              "Unable to load navigation."
            );

          }


          /*
          |--------------------------------------------------------------------------
          | MENU
          |--------------------------------------------------------------------------
          */

          const menu =
            Array.isArray(
              response
                ?.data
            )
              ?
              response.data
              :
              [];


          setNavigation(
            menu
          );

        } catch (
          error
        ) {

          console.error(
            "Sidebar navigation error:",
            error
          );


          setNavigation(
            []
          );


          setNavigationError(
            error
              ?.response
              ?.data
              ?.message ||

            error
              ?.message ||

            "Unable to load menu."
          );

        } finally {

          setNavigationLoading(
            false
          );

        }

      },
      []
    );


  /*
  |--------------------------------------------------------------------------
  | LOAD SIDEBAR DATA
  |--------------------------------------------------------------------------
  */

  useEffect(
    () => {

      /*
      |--------------------------------------------------------------------------
      | NOT LOGGED IN
      |--------------------------------------------------------------------------
      */

      if (
        !user
      ) {

        setNavigation(
          []
        );


        setNavigationLoading(
          false
        );


        return;

      }


      /*
      |--------------------------------------------------------------------------
      | NAVIGATION
      |--------------------------------------------------------------------------
      */

      loadNavigation();


      /*
      |--------------------------------------------------------------------------
      | CURRENT SUBSCRIPTION
      |--------------------------------------------------------------------------
      |
      | Do not request it repeatedly if SubscriptionGuard or another component
      | already initialized the subscription state.
      |
      |--------------------------------------------------------------------------
      */

      if (
        !subscriptionInitialized
      ) {

        dispatch(
          getCurrentSubscription()
        );

      }

    },
    [
      user?._id,
      user?.id,
      user?.roleId,
      user?.permissionMode,
      loadNavigation,
      subscriptionInitialized,
      dispatch,
    ]
  );


  /*
  |--------------------------------------------------------------------------
  | PREPARE BACKEND MENU ICONS
  |--------------------------------------------------------------------------
  */

  const menuData =
    useMemo(
      () =>
        prepareMenuData(
          navigation
        ),
      [
        navigation,
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | MEDIA QUERY
  |--------------------------------------------------------------------------
  */

  useEffect(
    () => {

      const media =
        window.matchMedia(
          `(min-width: ${DESKTOP_BREAKPOINT}px)`
        );


      const handleChange =
        (
          event
        ) => {

          setIsDesktop(
            event.matches
          );


          if (
            event.matches
          ) {

            onMobileOpenChange?.(
              false
            );

          } else {

            setHoverExpanded(
              false
            );

          }

        };


      setIsDesktop(
        media.matches
      );


      if (
        media.addEventListener
      ) {

        media.addEventListener(
          "change",
          handleChange
        );

      } else {

        media.addListener(
          handleChange
        );

      }


      return () => {

        if (
          media.removeEventListener
        ) {

          media.removeEventListener(
            "change",
            handleChange
          );

        } else {

          media.removeListener(
            handleChange
          );

        }

      };

    },
    [
      onMobileOpenChange,
    ]
  );


  /*
  |--------------------------------------------------------------------------
  | AUTO OPEN ACTIVE PARENT
  |--------------------------------------------------------------------------
  */

  useEffect(
    () => {

      const parent =
        menuData.find(
          (
            menu
          ) => {

            if (
              !Array.isArray(
                menu?.children
              )
            ) {

              return false;

            }


            return menu
              .children
              .some(
                (
                  child
                ) => {

                  const path =
                    String(
                      child
                        ?.path ||
                      ""
                    )
                      .trim();


                  if (
                    !path
                  ) {

                    return false;

                  }


                  return (
                    location.pathname ===
                      path ||

                    location.pathname
                      .startsWith(
                        `${path}/`
                      )
                  );

                }
              );

          }
        );


      if (
        parent
      ) {

        setExpandedMenu(
          parent?.key ||
          parent?.title ||
          ""
        );

      }

    },
    [
      location.pathname,
      menuData,
    ]
  );


  /*
  |--------------------------------------------------------------------------
  | BODY SCROLL LOCK ON MOBILE
  |--------------------------------------------------------------------------
  */

  useEffect(
    () => {

      if (
        !isDesktop &&
        mobileOpen
      ) {

        document.body.style.overflow =
          "hidden";

      } else {

        document.body.style.overflow =
          "";

      }


      return () => {

        document.body.style.overflow =
          "";

      };

    },
    [
      isDesktop,
      mobileOpen,
    ]
  );


  /*
  |--------------------------------------------------------------------------
  | CLOSE MOBILE
  |--------------------------------------------------------------------------
  */

  const closeMobile =
    () => {

      if (
        !isDesktop
      ) {

        onMobileOpenChange?.(
          false
        );

      }

    };


  /*
  |--------------------------------------------------------------------------
  | TOGGLE PARENT MENU
  |--------------------------------------------------------------------------
  */

  const toggleMenu =
    (
      menu
    ) => {

      const key =
        menu?.key ||
        menu?.title ||
        "";


      setExpandedMenu(
        (
          previous
        ) =>
          previous ===
            key
            ?
            ""
            :
            key
      );

    };


  /*
  |--------------------------------------------------------------------------
  | NORMAL MENU CLICK
  |--------------------------------------------------------------------------
  */

  const handleMenuClick =
    () => {

      closeMobile();


      if (
        isDesktop &&
        !pinnedOpen
      ) {

        setHoverExpanded(
          false
        );

      }

    };


  /*
  |--------------------------------------------------------------------------
  | LOGOUT
  |--------------------------------------------------------------------------
  */

  const handleLogout =
    async () => {

      try {

        await logout();

      } catch (
        error
      ) {

        console.error(
          "Logout error:",
          error
        );

      }

    };


  /*
  |--------------------------------------------------------------------------
  | MANAGE PLAN
  |--------------------------------------------------------------------------
  |
  | IMPORTANT:
  |
  | If your subscription page route is different, change ONLY this path.
  |
  |--------------------------------------------------------------------------
  */

  const handleManagePlan =
    () => {

      history.push(
        "/subscription"
      );


      closeMobile();


      if (
        isDesktop &&
        !pinnedOpen
      ) {

        setHoverExpanded(
          false
        );

      }

    };


  /*
  |--------------------------------------------------------------------------
  | SUBSCRIPTION DISPLAY
  |--------------------------------------------------------------------------
  */

  const planName =
    currentSubscription
      ?.planName ||

    currentSubscription
      ?.planSnapshot
      ?.name ||

    currentSubscription
      ?.planId
      ?.name ||

    currentSubscription
      ?.planCode ||

    currentSubscription
      ?.planSnapshot
      ?.code ||

    "No Plan";


  const planCode =
    currentSubscription
      ?.planCode ||

    currentSubscription
      ?.planSnapshot
      ?.code ||

    currentSubscription
      ?.planId
      ?.code ||

    planName;


  const planEndDate =
    currentSubscription
      ?.endsAt ||

    currentSubscription
      ?.endDate ||

    currentSubscription
      ?.expiresAt ||

    null;


  const planExpiryLabel =
    formatPlanDate(
      planEndDate
    );


  /*
  |--------------------------------------------------------------------------
  | SUBSCRIPTION STATUS
  |--------------------------------------------------------------------------
  */

  const subscriptionStatus =
    String(
      currentSubscription
        ?.status ||
      ""
    )
      .trim()
      .toUpperCase();


  /*
  |--------------------------------------------------------------------------
  | PLAN CARD AVAILABLE
  |--------------------------------------------------------------------------
  */

  const hasSubscription =
    Boolean(
      currentSubscription
    );


  /*
  |--------------------------------------------------------------------------
  | SIDEBAR CLASS
  |--------------------------------------------------------------------------
  */

  const sidebarClassName =
    [
      "admin-sidebar",

      isDesktop
        ?
        "desktop-mode"
        :
        "mobile-mode",

      desktopExpanded
        ?
        "expanded"
        :
        "collapsed",

      pinnedOpen
        ?
        "pinned"
        :
        "",

      !isDesktop &&
      mobileOpen
        ?
        "mobile-open"
        :
        "",
    ]
      .filter(
        Boolean
      )
      .join(
        " "
      );


  /*
  |--------------------------------------------------------------------------
  | RENDER CHILD
  |--------------------------------------------------------------------------
  */

  const renderChild =
    (
      child,
      childIndex
    ) => {

      const ChildIcon =
        child?.icon;


      const childKey =
        child?.key ||
        child?.path ||
        childIndex;


      /*
      |--------------------------------------------------------------------------
      | INVALID PATH
      |--------------------------------------------------------------------------
      */

      if (
        !child?.path
      ) {

        return null;

      }


      return (

        <li
          key={
            childKey
          }
        >

          <NavLink

            exact={
              child?.exact ??
              true
            }

            to={
              child.path
            }

            activeClassName="active-link"

            className="admin-submenu-link"

            onClick={
              handleMenuClick
            }

          >

            {
              ChildIcon
                ?
                (
                  <ChildIcon />
                )
                :
                null
            }


            <span>
              {
                child?.title
              }
            </span>

          </NavLink>

        </li>

      );

    };


  /*
  |--------------------------------------------------------------------------
  | RENDER MENU ITEM
  |--------------------------------------------------------------------------
  */

  const renderMenuItem =
    (
      menu,
      index
    ) => {

      const Icon =
        menu?.icon;


      const hasChildren =
        Array.isArray(
          menu?.children
        ) &&
        menu.children.length >
          0;


      const menuKey =
        menu?.key ||
        menu?.title ||
        index;


      const isExpanded =
        expandedMenu ===
        menuKey;


      /*
      |--------------------------------------------------------------------------
      | PARENT MENU
      |--------------------------------------------------------------------------
      */

      if (
        hasChildren
      ) {

        return (

          <li
            key={
              menuKey
            }

            className="admin-menu-item"
          >

            <button

              type="button"

              className={
                isExpanded
                  ?
                  "admin-menu-parent expanded-menu"
                  :
                  "admin-menu-parent"
              }

              onClick={
                () =>
                  toggleMenu(
                    menu
                  )
              }

              title={
                !desktopExpanded &&
                isDesktop
                  ?
                  menu?.title
                  :
                  ""
              }

            >

              <span className="admin-menu-left">

                {
                  Icon
                    ?
                    (
                      <Icon className="admin-menu-icon" />
                    )
                    :
                    null
                }


                {
                  showSidebarText
                    ?
                    (
                      <span className="admin-menu-title">
                        {
                          menu?.title
                        }
                      </span>
                    )
                    :
                    null
                }

              </span>


              {
                showSidebarText
                  ?
                  (
                    <span className="admin-menu-arrow">

                      {
                        isExpanded
                          ?
                          (
                            <FaChevronUp />
                          )
                          :
                          (
                            <FaChevronDown />
                          )
                      }

                    </span>
                  )
                  :
                  null
              }

            </button>


            {
              showSidebarText
                ?
                (
                  <ul
                    className={
                      isExpanded
                        ?
                        "admin-submenu show"
                        :
                        "admin-submenu"
                    }
                  >

                    {
                      menu
                        .children
                        .map(
                          renderChild
                        )
                    }

                  </ul>
                )
                :
                null
            }

          </li>

        );

      }


      /*
      |--------------------------------------------------------------------------
      | IMPORTANT
      |--------------------------------------------------------------------------
      |
      | Logout is no longer expected from backend menu.
      |
      | But if an old backend response still contains LOGOUT, skip it so we
      | don't render duplicate logout buttons.
      |
      |--------------------------------------------------------------------------
      */

      const isLogout =
        String(
          menu?.key ||
          menu?.title ||
          ""
        )
          .trim()
          .toUpperCase() ===
        "LOGOUT";


      if (
        isLogout
      ) {

        return null;

      }


      /*
      |--------------------------------------------------------------------------
      | INVALID NORMAL MENU
      |--------------------------------------------------------------------------
      */

      if (
        !menu?.path
      ) {

        return null;

      }


      /*
      |--------------------------------------------------------------------------
      | NORMAL MENU
      |--------------------------------------------------------------------------
      */

      return (

        <li
          key={
            menuKey
          }

          className="admin-menu-item"
        >

          <NavLink

            exact={
              menu?.exact ??
              true
            }

            to={
              menu.path
            }

            activeClassName="active-link"

            className="admin-menu-link"

            title={
              !desktopExpanded &&
              isDesktop
                ?
                menu?.title
                :
                ""
            }

            onClick={
              handleMenuClick
            }

          >

            {
              Icon
                ?
                (
                  <Icon className="admin-menu-icon" />
                )
                :
                null
            }


            {
              showSidebarText
                ?
                (
                  <span className="admin-menu-title">
                    {
                      menu?.title
                    }
                  </span>
                )
                :
                null
            }

          </NavLink>

        </li>

      );

    };


  /*
  |--------------------------------------------------------------------------
  | UI
  |--------------------------------------------------------------------------
  */

  return (

    <>

      {/* ================================================================
          MOBILE OVERLAY
      ================================================================ */}

      {
        !isDesktop &&
        mobileOpen
          ?
          (
            <div

              className="admin-sidebar-overlay"

              onClick={
                closeMobile
              }

            />
          )
          :
          null
      }


      {/* ================================================================
          SIDEBAR
      ================================================================ */}

      <aside

        className={
          sidebarClassName
        }

        onMouseEnter={
          () => {

            if (
              isDesktop &&
              !pinnedOpen
            ) {

              setHoverExpanded(
                true
              );

            }

          }
        }

        onMouseLeave={
          () => {

            if (
              isDesktop &&
              !pinnedOpen
            ) {

              setHoverExpanded(
                false
              );

            }

          }
        }

      >

        {/* ============================================================
            HEADER
        ============================================================ */}

        <div className="admin-sidebar-header">

          <div className="admin-profile">

            <FaUserCircle className="admin-profile-icon" />


            {
              showSidebarText
                ?
                (
                  <div className="admin-profile-info">

                    <h4>

                      {
                        user?.name ||
                        user?.mobile ||
                        "Administrator"
                      }

                    </h4>


                    <span>

                      {
                        user
                          ?.role
                          ?.name ||

                        user
                          ?.roleName ||

                        (
                          String(
                            user
                              ?.userType ||
                            ""
                          )
                            .trim()
                            .toUpperCase() ===
                          "OWNER"
                            ?
                            "Owner"
                            :
                            "Administrator"
                        )
                      }

                    </span>

                  </div>
                )
                :
                null
            }

          </div>


          {/* ========================================================
              MOBILE CLOSE
          ======================================================== */}

          {
            !isDesktop
              ?
              (
                <button

                  type="button"

                  className="admin-sidebar-action"

                  onClick={
                    closeMobile
                  }

                  title="Close"

                >

                  <FaTimes />

                </button>
              )
              :
              null
          }


          {/* ========================================================
              DESKTOP PIN
          ======================================================== */}

          {
            isDesktop &&
            desktopExpanded
              ?
              (
                <button

                  type="button"

                  className={
                    pinnedOpen
                      ?
                      "admin-sidebar-action pin active"
                      :
                      "admin-sidebar-action pin"
                  }

                  onClick={
                    () => {

                      onPinnedOpenChange?.(
                        !pinnedOpen
                      );


                      setHoverExpanded(
                        false
                      );

                    }
                  }

                  title={
                    pinnedOpen
                      ?
                      "Unpin sidebar"
                      :
                      "Keep sidebar open"
                  }

                >

                  <FaThumbtack />

                </button>
              )
              :
              null
          }

        </div>


        {/* ============================================================
            NAVIGATION
        ============================================================ */}

        <nav className="admin-sidebar-nav">

          {/* ========================================================
              LOADING
          ======================================================== */}

          {
            navigationLoading
              ?
              (
                <div className="admin-sidebar-menu-state">

                  <span className="admin-sidebar-menu-loader" />


                  {
                    showSidebarText
                      ?
                      (
                        <span>
                          Loading menu...
                        </span>
                      )
                      :
                      null
                  }

                </div>
              )
              :
              null
          }


          {/* ========================================================
              ERROR
          ======================================================== */}

          {
            !navigationLoading &&
            navigationError
              ?
              (
                <div className="admin-sidebar-menu-error">

                  {
                    showSidebarText
                      ?
                      (
                        <>

                          <FaExclamationTriangle />


                          <span>
                            {
                              navigationError
                            }
                          </span>


                          <button

                            type="button"

                            onClick={
                              loadNavigation
                            }

                          >

                            <FaRedoAlt />

                            Retry

                          </button>

                        </>
                      )
                      :
                      (
                        <button

                          type="button"

                          title="Retry menu"

                          onClick={
                            loadNavigation
                          }

                        >

                          <FaRedoAlt />

                        </button>
                      )
                  }

                </div>
              )
              :
              null
          }


          {/* ========================================================
              BACKEND MENU
          ======================================================== */}

          {
            !navigationLoading &&
            !navigationError &&
            menuData.length >
              0
              ?
              (
                <ul className="admin-menu">

                  {
                    menuData.map(
                      renderMenuItem
                    )
                  }

                </ul>
              )
              :
              null
          }


          {/* ========================================================
              EMPTY
          ======================================================== */}

          {
            !navigationLoading &&
            !navigationError &&
            menuData.length ===
              0
              ?
              (
                <div className="admin-sidebar-menu-empty">

                  {
                    showSidebarText
                      ?
                      "No menu access available."
                      :
                      null
                  }

                </div>
              )
              :
              null
          }

        </nav>


        {/* ============================================================
            SIDEBAR BOTTOM
        ============================================================ */}

        <div className="admin-sidebar-bottom">

          {/* ========================================================
              CURRENT PLAN - LOADING
          ======================================================== */}

          {
            subscriptionLoading
              ?
              (
                <div
                  className={
                    showSidebarText
                      ?
                      "admin-plan-card loading"
                      :
                      "admin-plan-card collapsed loading"
                  }
                >

                  <span className="admin-plan-crown">
                    <FaCrown />
                  </span>


                  {
                    showSidebarText
                      ?
                      (
                        <span className="admin-plan-loading-text">
                          Loading plan...
                        </span>
                      )
                      :
                      null
                  }

                </div>
              )
              :
              null
          }


          {/* ========================================================
              CURRENT PLAN
          ======================================================== */}

          {
            !subscriptionLoading &&
            hasSubscription
              ?
              (
                <div
                  className={
                    showSidebarText
                      ?
                      "admin-plan-card"
                      :
                      "admin-plan-card collapsed"
                  }
                >

                  {/* ================================================
                      PLAN HEADER
                  ================================================ */}

                  <div className="admin-plan-header">

                    <span className="admin-plan-crown">

                      <FaCrown />

                    </span>


                    {
                      showSidebarText
                        ?
                        (
                          <div className="admin-plan-heading">

                            <small>
                              YOUR CURRENT PLAN
                            </small>


                            <strong>

                              {
                                String(
                                  planCode ||
                                  planName
                                )
                                  .trim()
                                  .toUpperCase()
                              }

                            </strong>


                            {
                              subscriptionStatus
                                ?
                                (
                                  <span className="admin-plan-status">
                                    {
                                      subscriptionStatus
                                    }
                                  </span>
                                )
                                :
                                null
                            }

                          </div>
                        )
                        :
                        null
                    }

                  </div>


                  {/* ================================================
                      EXPIRY
                  ================================================ */}

                  {
                    showSidebarText &&
                    planExpiryLabel
                      ?
                      (
                        <div className="admin-plan-expiry">

                          Expires on{" "}

                          <strong>
                            {
                              planExpiryLabel
                            }
                          </strong>

                        </div>
                      )
                      :
                      null
                  }


                  {/* ================================================
                      MANAGE PLAN
                  ================================================ */}

                  {
                    showSidebarText
                      ?
                      (
                        <button

                          type="button"

                          className="admin-plan-manage"

                          onClick={
                            handleManagePlan
                          }

                        >

                          Manage Plan

                        </button>
                      )
                      :
                      null
                  }

                </div>
              )
              :
              null
          }


          {/* ========================================================
              NO SUBSCRIPTION
          ======================================================== */}

          {
            !subscriptionLoading &&
            subscriptionInitialized &&
            !hasSubscription &&
            showSidebarText
              ?
              (
                <div className="admin-plan-card no-plan">

                  <div className="admin-plan-header">

                    <span className="admin-plan-crown">
                      <FaCrown />
                    </span>


                    <div className="admin-plan-heading">

                      <small>
                        SUBSCRIPTION
                      </small>

                      <strong>
                        NO ACTIVE PLAN
                      </strong>

                    </div>

                  </div>


                  <button

                    type="button"

                    className="admin-plan-manage"

                    onClick={
                      handleManagePlan
                    }

                  >

                    View Plans

                  </button>

                </div>
              )
              :
              null
          }


          {/* ========================================================
              LOGOUT
          ======================================================== */}

          <button

            type="button"

            className={
              showSidebarText
                ?
                "admin-sidebar-logout"
                :
                "admin-sidebar-logout collapsed"
            }

            onClick={
              handleLogout
            }

            title="Logout"

          >

            <span className="admin-sidebar-logout-icon">

              <FaSignOutAlt />

            </span>


            {
              showSidebarText
                ?
                (
                  <span className="admin-sidebar-logout-text">
                    Logout
                  </span>
                )
                :
                null
            }

          </button>

        </div>

      </aside>

    </>

  );

};


export default AdminSidebar;